import { useState } from 'react';
import api from '../api/axios';

export default function CallbackModal({ service, onClose }) {
  const [step, setStep] = useState(1); // 1 = Info, 2 = Form
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    service: service?.name || '',
    message: `I would like to request a callback for ${service?.name}.`
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInput = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.fullName || !form.phone) return;
    setLoading(true);
    try {
      await api.post('/api/enquiry/submit', form);
      setSubmitted(true);
    } catch {
      alert('Failed to submit callback request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!service) return null;

  return (
    <>
      <style>{`
        .cm-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(7, 15, 30, 0.7);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: cmFadeIn 0.25s ease-out;
        }
        .cm-window {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 20px;
          box-shadow: 0 20px 50px rgba(7,15,30,0.3);
          max-width: 500px;
          width: 100%;
          overflow: hidden;
          animation: cmSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          font-family: 'DM Sans', sans-serif;
        }
        @keyframes cmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes cmSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cm-header {
          background: var(--navy);
          padding: 1.5rem;
          color: #fff;
          position: relative;
        }
        .cm-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.35rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }
        .cm-category {
          display: inline-block;
          background: rgba(201,146,42,0.2);
          color: var(--goldt);
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0.2rem 0.55rem;
          border-radius: 4px;
          font-weight: 700;
        }
        .cm-close-btn {
          position: absolute;
          top: 1.25rem;
          right: 1.25rem;
          background: none;
          border: none;
          color: rgba(255,255,255,0.6);
          font-size: 1.3rem;
          cursor: pointer;
          transition: color 0.15s;
          line-height: 1;
        }
        .cm-close-btn:hover {
          color: #fff;
        }
        .cm-body {
          padding: 1.75rem;
          max-height: 70vh;
          overflow-y: auto;
        }
        .cm-desc {
          font-size: 0.92rem;
          color: var(--text);
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }
        .cm-bullet-title {
          font-family: 'Playfair Display', serif;
          font-size: 0.95rem;
          color: var(--navy);
          font-weight: 700;
          margin-bottom: 0.5rem;
          border-bottom: 1px solid var(--border);
          padding-bottom: 0.25rem;
        }
        .cm-bullets {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem;
        }
        .cm-bullets li {
          font-size: 0.85rem;
          color: var(--muted);
          padding: 0.35rem 0;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .cm-bullets li::before {
          content: '✓';
          color: var(--green);
          font-weight: bold;
        }
        .cm-footer {
          display: flex;
          gap: 0.75rem;
          border-top: 1px solid var(--border);
          padding: 1.25rem 1.75rem;
          background: var(--cream);
          justify-content: flex-end;
        }
        .cm-btn {
          padding: 0.7rem 1.4rem;
          font-size: 0.88rem;
          font-weight: 700;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .cm-btn-secondary {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--muted);
        }
        .cm-btn-secondary:hover {
          background: rgba(7, 15, 30, 0.05);
          color: var(--navy);
        }
        .cm-btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .cm-btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(201,146,42,0.25);
        }
        .cm-field {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          margin-bottom: 1rem;
        }
        .cm-field label {
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--navy);
        }
        .cm-field input, .cm-field textarea {
          padding: 0.65rem 0.85rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 0.88rem;
          font-family: 'DM Sans', sans-serif;
          transition: border-color 0.15s;
        }
        .cm-field input:focus, .cm-field textarea:focus {
          outline: none;
          border-color: var(--gold);
        }
        .cm-success {
          text-align: center;
          padding: 1rem 0;
        }
        .cm-success-icon {
          font-size: 3rem;
          margin-bottom: 0.75rem;
        }
        .cm-success-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.4rem;
          color: var(--navy);
          margin-bottom: 0.5rem;
        }
        .cm-success-p {
          font-size: 0.9rem;
          color: var(--muted);
          line-height: 1.5;
        }
      `}</style>

      <div className="cm-backdrop" onClick={onClose}>
        <div className="cm-window" onClick={e => e.stopPropagation()}>
          
          {/* Header */}
          <div className="cm-header">
            <button className="cm-close-btn" onClick={onClose}>✕</button>
            <div className="cm-title">{service.name}</div>
            <div className="cm-category">{service.category}</div>
          </div>

          {/* Body */}
          <div className="cm-body">
            {submitted ? (
              <div className="cm-success">
                <div className="cm-success-icon">✅</div>
                <div className="cm-success-title">Request Received!</div>
                <p className="cm-success-p">
                  Thank you for your interest. A certified CA expert will call you back on <strong>{form.phone}</strong> within 2 hours.
                </p>
              </div>
            ) : step === 1 ? (
              <div>
                <p className="cm-desc">{service.description || 'Consult with our expert Chartered Accountants for quick processing and advice.'}</p>
                <div className="cm-bullet-title">What is Included:</div>
                <ul className="cm-bullets">
                  <li>Expert CA Consultation and Document Verification</li>
                  <li>Secure document submission & verification</li>
                  <li>Timely filing status notifications & draft review</li>
                  <li>Secure digital archiving of all tax receipts & certificates</li>
                </ul>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="cm-callback-form">
                <div className="cm-field">
                  <label>Full Name *</label>
                  <input
                    name="fullName"
                    placeholder="Rahul Sharma"
                    value={form.fullName}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className="cm-field">
                  <label>Mobile Number *</label>
                  <input
                    name="phone"
                    placeholder="9876543210"
                    value={form.phone}
                    onChange={handleInput}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="cm-field">
                  <label>Email Address</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="rahul@example.com"
                    value={form.email}
                    onChange={handleInput}
                  />
                </div>
                <div className="cm-field">
                  <label>Message (optional)</label>
                  <textarea
                    name="message"
                    rows={2}
                    value={form.message}
                    onChange={handleInput}
                    placeholder="Briefly state your requirement..."
                  />
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="cm-footer">
            {submitted ? (
              <button className="cm-btn cm-btn-primary" onClick={onClose}>Close</button>
            ) : step === 1 ? (
              <>
                <button className="cm-btn cm-btn-secondary" onClick={onClose}>Close</button>
                <button className="cm-btn cm-btn-primary" onClick={() => setStep(2)}>
                  Request Callback 📞
                </button>
              </>
            ) : (
              <>
                <button className="cm-btn cm-btn-secondary" onClick={() => setStep(1)} disabled={loading}>
                  Back
                </button>
                <button
                  type="submit"
                  form="cm-callback-form"
                  className="cm-btn cm-btn-primary"
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  {loading ? 'Sending...' : 'Submit Request'}
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
